import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getBrokerContracts,
  getBrokers,
  searchCustomerCompanies,
  createBrokerContract,
  updateBrokerContract,
  deactivateBrokerContract,
} from "api/brokerApi";
import { unwrapList, unwrapObject } from "api/unwrap";

const emptyForm = {
  customerCompanyId: "",
  brokerId: "",
  contractNumber: "",
  contractEffectiveAt: null,
  contractExpiresAt: null,
  notes: "",
  active: true,
};

const dateToInputValue = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  return d.toISOString().slice(0, 10);
};
const inputValueToMs = (s) => {
  if (!s) return null;
  return new Date(s + "T00:00:00Z").getTime();
};

const ContractEditDialog = ({ open, onClose, contract, brokers, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [companies, setCompanies] = useState([]);
  const [companyQuery, setCompanyQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (contract) {
      setForm({
        customerCompanyId: contract.customerCompanyId || "",
        brokerId: contract.brokerId || "",
        contractNumber: contract.contractNumber || "",
        contractEffectiveAt: contract.contractEffectiveAt || null,
        contractExpiresAt: contract.contractExpiresAt || null,
        notes: contract.notes || "",
        active: contract.active ?? true,
      });
      if (contract.customerCompanyName) {
        setCompanies([{ id: contract.customerCompanyId, companyName: contract.customerCompanyName }]);
      }
    } else {
      setForm(emptyForm);
    }
  }, [open, contract]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await searchCustomerCompanies(companyQuery);
        const inner = unwrapObject(res);
        const list = inner?.content ?? inner ?? [];
        if (!cancelled) setCompanies(Array.isArray(list) ? list : []);
      } catch (e) {
        // keep previous; not fatal
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyQuery]);

  const handleSave = async () => {
    if (!form.customerCompanyId || !form.brokerId) {
      setError("Pick a company and a broker");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = contract?.id
        ? await updateBrokerContract(contract.id, form)
        : await createBrokerContract(form);
      if (res?.error) {
        setError(res.error);
        return;
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const selectedCompany = companies.find((c) => c.id === form.customerCompanyId) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {contract ? "Edit Contract" : "Add Broker Contract"}
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert> : null}
        <Stack spacing={2}>
          <Autocomplete
            options={companies}
            value={selectedCompany}
            getOptionLabel={(o) => o.companyName || ""}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onInputChange={(_, v) => setCompanyQuery(v)}
            onChange={(_, v) => setForm({ ...form, customerCompanyId: v?.id || "" })}
            renderInput={(params) => (
              <TextField {...params} label="Customer Company" size="small" />
            )}
          />
          <TextField
            select
            label="Broker"
            size="small"
            value={form.brokerId}
            onChange={(e) => setForm({ ...form, brokerId: e.target.value })}
          >
            {brokers.map((b) => (
              <MenuItem key={b.id} value={b.id}>{b.name} ({b.code})</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Contract Number"
            size="small"
            value={form.contractNumber}
            onChange={(e) => setForm({ ...form, contractNumber: e.target.value })}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Effective"
              type="date"
              size="small"
              value={dateToInputValue(form.contractEffectiveAt)}
              onChange={(e) => setForm({ ...form, contractEffectiveAt: inputValueToMs(e.target.value) })}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Expires"
              type="date"
              size="small"
              value={dateToInputValue(form.contractExpiresAt)}
              onChange={(e) => setForm({ ...form, contractExpiresAt: inputValueToMs(e.target.value) })}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Box>
          <TextField
            label="Notes"
            size="small"
            multiline
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
          sx={{ backgroundColor: "#1F4E78" }}
        >
          {contract ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const BrokerContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, b] = await Promise.all([getBrokerContracts(), getBrokers()]);
      setContracts(unwrapList(c));
      setBrokers(unwrapList(b));
    } catch (e) {
      setError(e?.message || "Failed to load contracts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return contracts;
    return contracts.filter((c) => c.brokerId === filter);
  }, [contracts, filter]);

  const handleDeactivate = async (c) => {
    if (!window.confirm(`Deactivate contract: ${c.customerCompanyName || "?"} ↔ ${c.brokerName || "?"}?`)) return;
    try {
      await deactivateBrokerContract(c.id);
      load();
    } catch (e) {
      setError(e?.message || "Deactivate failed");
    }
  };

  return (
    <Stack
      spacing={2}
      sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Broker Contracts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Bind customer companies to brokers. Drivers servicing these companies inherit the broker's required trainings + policies.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditing("new")}
            sx={{ backgroundColor: "#1F4E78" }}
          >
            Add Contract
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        <Chip
          size="small"
          label={`All (${contracts.length})`}
          onClick={() => setFilter("all")}
          sx={{
            backgroundColor: filter === "all" ? "#1F4E78" : "#f1f5f9",
            color: filter === "all" ? "#ffffff" : "#475569",
            fontWeight: filter === "all" ? 700 : 500,
            cursor: "pointer",
          }}
        />
        {brokers.map((b) => {
          const count = contracts.filter((c) => c.brokerId === b.id).length;
          if (count === 0) return null;
          return (
            <Chip
              key={b.id}
              size="small"
              label={`${b.code} (${count})`}
              onClick={() => setFilter(b.id)}
              sx={{
                backgroundColor: filter === b.id ? "#1F4E78" : "#f1f5f9",
                color: filter === b.id ? "#ffffff" : "#475569",
                fontWeight: filter === b.id ? 700 : 500,
                cursor: "pointer",
              }}
            />
          );
        })}
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      {loading && contracts.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              {contracts.length === 0
                ? "No contracts yet. Click 'Add Contract' to bind your first company to a broker."
                : "No contracts match the current filter."}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        filtered.map((c) => (
          <Card key={c.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {c.customerCompanyName || "(company)"}
                </Typography>
                <Typography variant="body2" color="text.secondary">↔</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {c.brokerName || c.brokerCode || "(broker)"}
                </Typography>
                <Chip size="small" label={c.brokerCode} sx={{ ml: 1, fontWeight: 600 }} />
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" onClick={() => setEditing(c)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => handleDeactivate(c)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                {c.contractNumber ? (
                  <Typography variant="caption" color="text.secondary">
                    Contract #: {c.contractNumber}
                  </Typography>
                ) : null}
                {c.contractEffectiveAt ? (
                  <Typography variant="caption" color="text.secondary">
                    Effective: {new Date(c.contractEffectiveAt).toLocaleDateString()}
                  </Typography>
                ) : null}
                {c.contractExpiresAt ? (
                  <Typography variant="caption" color="text.secondary">
                    Expires: {new Date(c.contractExpiresAt).toLocaleDateString()}
                  </Typography>
                ) : null}
              </Box>
              {c.notes ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontStyle: "italic" }}>
                  {c.notes}
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        ))
      )}

      <ContractEditDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        contract={editing === "new" ? null : editing}
        brokers={brokers}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    </Stack>
  );
};

export default BrokerContractsPage;
